import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import NotificationPredefinedTemplatesModalComponent
    from '@/modules/configurationModule/detail/notifications/components/notification-predefined-templates-modal/notification-predefined-templates-modal.vue'
import NotificationPredefinedTemplatesModal from '@/modules/configurationModule/detail/notifications/components/notification-predefined-templates-modal/notification-predefined-templates-modal'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import {
    columnsDefNotificationTypesGrid,
    initialNotificationsPredefinedTemplatesGridState,
    NotificationsPredefinedTemplatesGridState
} from '@/modules/configurationModule/detail/notifications/components/notification-predefined-templates-grid/notification-predefined-templates-grid.model'
import {
    NotificationPredefinedTemplatesCreateRequest,
    NotificationPredefinedTemplatesUpdateRequest
} from '@/modules/configurationModule/detail/notifications/models/notification-predefined-templates'
import { predefinedCustomNotificationConfigurationService } from '@/modules/configurationModule/detail/notifications/services/predefined-custom-notification/predefined-custom-notification.service'
import { NotificationPredefinedTemplatesModalState } from '@/modules/configurationModule/detail/notifications/components/notification-predefined-templates-modal/notification-predefined-templates-modal.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import { PredefinedCustomNotificationResponse } from '@/modules/configurationModule/detail/notifications/services/predefined-custom-notification/predefined-custom-notification.dto'
import { NotificationTemplatesModalType } from '@/modules/configurationModule/detail/notifications/components/notification-templates-modal/notification-templates-modal.model'
import { customNotificationTypeService } from '@/modules/configurationModule/detail/notifications/services/custom-notification-type.service'

@Component({
    components: {
        NotificationPredefinedTemplatesModalComponent,
        ConfigurationSectionHeader,
        VtGrid
    }
})
export default class NotificationPredefinedTemplatesGridComponent extends BaseCrudServerSideComponent<PredefinedCustomNotificationResponse> implements BaseCrud {
    @Ref() readonly notificationTemplatesPredefinedModalWrapper!: any
    @Ref() readonly notificationTemplatesPredefinedModal!: NotificationPredefinedTemplatesModal

    state: NotificationsPredefinedTemplatesGridState
    constructor() {
        super()
        this.state = initialNotificationsPredefinedTemplatesGridState()
    }

    private async _getCustomNotificationTypes(): Promise<NotificationTemplatesModalType[]> {
        const response =
            await this.$_toCallSecure(
                customNotificationTypeService.getCustomNotificationTypes())
        return response ?? []
    }

    async mounted() {
        const customNotificationTypes = await this._getCustomNotificationTypes()
        this.state.columnsDef = columnsDefNotificationTypesGrid(customNotificationTypes)
    }

    get laboratoryId(): number {
        return this.state.laboratoryId
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:PredefinedCustomNotificationResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.notificationTemplatesPredefinedModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            }
        ]
    }
    async createOperation(predefinedTemplate: NotificationPredefinedTemplatesModalState): Promise<void> {
        const predefinedTemplateToCreate: NotificationPredefinedTemplatesCreateRequest = {
            active: predefinedTemplate.active,
            attachReport: predefinedTemplate.attachReport,
            body: predefinedTemplate.body,
            personnelToNotifyIds: predefinedTemplate.personnelToBeNotifiedList.map(personnel => personnel.id),
            responsibilitiesToNotify: predefinedTemplate.responsibilitiesToNotify.map(responsibility => responsibility.code),
            shouldNotifyDepartment: predefinedTemplate.shouldNotifyDepartment,
            shouldNotifyPetitioner: predefinedTemplate.shouldNotifyPetitioner,
            subject: predefinedTemplate.subject,
            title: predefinedTemplate.title,
            type: predefinedTemplate.type?.code
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(predefinedCustomNotificationConfigurationService.create(predefinedTemplateToCreate, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.notificationTemplatesPredefinedModalWrapper?.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(predefinedTemplate: NotificationPredefinedTemplatesModalState): Promise<void> {
        const predefinedTemplateToPatch: NotificationPredefinedTemplatesUpdateRequest = {
            id: predefinedTemplate.id,
            active: predefinedTemplate.active,
            attachReport: predefinedTemplate.attachReport,
            body: predefinedTemplate.body,
            personnelToNotifyIds: predefinedTemplate.personnelToBeNotifiedList.map(personnel => personnel.id),
            responsibilitiesToNotify: predefinedTemplate.responsibilitiesToNotify.map(responsibility => responsibility.code),
            shouldNotifyDepartment: predefinedTemplate.shouldNotifyDepartment,
            shouldNotifyPetitioner: predefinedTemplate.shouldNotifyPetitioner,
            subject: predefinedTemplate.subject,
            title: predefinedTemplate.title
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(predefinedCustomNotificationConfigurationService.patch([predefinedTemplateToPatch], this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.notificationTemplatesPredefinedModalWrapper?.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteBatchOperation(predefinedTemplates: PredefinedCustomNotificationResponse[]): Promise<void> {
        const predefinedTemplatesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: predefinedTemplates.map((predefinedTemplate: PredefinedCustomNotificationResponse) => Number(predefinedTemplate?.id))
        }
        const [err, response] = await this.$_toCall(predefinedCustomNotificationConfigurationService.delete(predefinedTemplatesToDelete, this.laboratoryId))
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        if (predefinedTemplates.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: predefinedTemplates?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(predefinedTemplates: PredefinedCustomNotificationResponse[]): Promise<void> {
        if (predefinedTemplates.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                await this.deleteBatchOperation(predefinedTemplates)
            }
        }
    }
}
